import React from "react";
import { Box, GlobalStyles } from "@mui/material";
import {
  ContractTypeFilterBar,
  SalesVsTargetCard,
  PeriodTrendCard,
  PredictiveServicesCard,
  ActiveAccountsCard,
  OpsRingWidget,
  RankListWidget,
  SalesChartWidget,
  NetSalesAnalyticsWidget,
  CollectionsOutstandingWidget,
  UserLoginStatsWidget,
  ReceivableAgingWidget,
  ClientOutstandingListWidget,
  StationUtilizationListWidget,
} from "./components";
import { GRID, gridSx, pageSx } from "./components/exdMui";
import { dashMainViewScrollbarStyles } from "../shared/dashboardEnterprise";

const tooltipGlobals = (
  <GlobalStyles
    styles={{
      ".highcharts-tooltip-container": {
        pointerEvents: "none !important",
        zIndex: "10050 !important",
      },
      ".highcharts-tooltip": { zIndex: "10050 !important" },
      ".recharts-tooltip-wrapper": { zIndex: "10050 !important" },
      ".MuiTooltip-popper": { zIndex: "10060 !important" },
      /* Keep a single page scroll on ExDashboard (.main-view only) */
      "html, body": {
        overflow: "hidden",
        height: "100%",
      },
      ...dashMainViewScrollbarStyles,
      ".has-padding--single-scroll": {
        overflow: "visible !important",
        maxHeight: "none !important",
      },
    }}
  />
);

const ExDashboardView = ({
  translate,
  loginUserCurrency,
  selectedContractTypes,
  handleContractTypeChange,
  handleContractTypeSearch,
  handleChange,
  loader1,
  loader2,
  loader3,
  loader4,
  loader4a,
  loader5,
  loader6,
  loader6b,
  loader7,
  loader8,
  loader9,
  loader10,
  loader11,
  loader12,
  loader14,
  loader15,
  loader16,
  loader17,
  loader20,
  allSaleData,
  currentYearSale,
  avgTicketPrice,
  businessPotential,
  accountsData,
  grossProfit,
  vehiclesData,
  fleetCountChart2,
  fleetCountChart,
  cogsMonthly,
  date,
  cogsChartData,
  handleBarClickWrapper,
  handleCogsChartBack,
  sparePartsChartNew,
  showAllInventory,
  handleShowAllInventoryChange,
  servicesPartsChartData,
  showAllServices,
  handleShowAllServicesChange,
  vehiclesChart,
  showAllVehicles,
  handleShowAllVehiclesChange,
  garageDataType,
  verticalWiseChart,
  areaWiseChart,
  stationWiseChart,
  areaWiseData,
  handleNetSalesAnalytics,
  userLogsGraphData,
  collectionsAndOutStanding,
  collectionsAndOutStandingData,
  overDue,
  overDueChart,
  showAccountsData,
  showAccountsTitle,
  showAccountsLoader,
  accountsDataChart,
  handleAccountsBack,
  outstandingSummary,
  topologyData,
  tab1,
  tab2,
  tab5,
  tab8,
  tab9,
  tab10,
}) => (
  <Box sx={pageSx}>
    {tooltipGlobals}
    <ContractTypeFilterBar
      selectedContractTypes={selectedContractTypes}
      onChange={handleContractTypeChange}
      onSearch={handleContractTypeSearch}
      translate={translate}
    />

    <Box component="section" aria-label="Key figures" sx={gridSx(GRID.kpi)}>
      <SalesVsTargetCard
        title={translate("Monthly Sales")}
        saleAmount={allSaleData?.monthlySale}
        targetAmount={allSaleData?.monthlyTarget}
        saleLabel={translate("Monthly Sales")}
        targetLabel={translate("Monthly Target")}
        infoTitle={translate("Current Month DNs Amount (with Vat)")}
        loading={!!loader1}
        translate={translate}
        loginUserCurrency={loginUserCurrency}
        accent="revenue"
      />
      <SalesVsTargetCard
        title={translate("Yearly Sales")}
        saleAmount={allSaleData?.yearlySale}
        targetAmount={allSaleData?.yearlyTarget}
        saleLabel={translate("CYTD Sales")}
        targetLabel={translate("Annual Target")}
        infoTitle={translate(
          "Current Year manual Invoices (with VAT) + Current Year Valid DNs (with VAT) + debit notes - credit notes",
        )}
        loading={!!loader1}
        translate={translate}
        loginUserCurrency={loginUserCurrency}
        accent="throughput"
      />
      <PeriodTrendCard
        title={translate("Sale Performance Trend")}
        currentValue={currentYearSale?.currentYearSale}
        priorValue={currentYearSale?.lastYearSale}
        currentLabel={translate("currentMonth")}
        priorLabel={translate("prior month")}
        infoTitle={translate(
          "Current Month vs Prior Month Invoice Amount + Current Month Valid DNs (with Vat)",
        )}
        loading={!!loader2}
        loginUserCurrency={loginUserCurrency}
        accent="performance"
        showSparkline
        sparklineData={[
          currentYearSale?.lastYearSale,
          currentYearSale?.currentYearSale,
        ]}
      />
      <PeriodTrendCard
        title={translate("Average Ticket Price Trend")}
        currentValue={avgTicketPrice?.ytdAverageTicket}
        priorValue={avgTicketPrice?.lyAverageTicket}
        currentLabel={translate("currentMonth")}
        priorLabel={translate("prior month")}
        infoTitle={translate(
          "Current Month average ticket price vs prior Month Total DNs Amount Divided By Total DNs Count (with Vat)",
        )}
        loading={!!loader10}
        loginUserCurrency={loginUserCurrency}
        accent="pms"
      />
      <PredictiveServicesCard
        loading={!!loader15}
        translate={translate}
        businessPotential={businessPotential}
        loginUserCurrency={loginUserCurrency}
        infoTitle={translate(
          "Past 10 days to future 20 days Total Predicted Services, Past 10 days to future 20 days Total Predicted Services Amount",
        )}
        accent="outstanding"
      />
      <ActiveAccountsCard
        loading={!!loader5}
        translate={translate}
        accountsData={accountsData}
        infoTitle={translate(
          "Total Accounts, Past 30 days Newly Created Accounts",
        )}
        accent="risk"
      />
    </Box>

    <Box component="section" aria-label="Operations" sx={gridSx(GRID.ops)}>
      <OpsRingWidget
        title={translate("Monthly Gross Profit")}
        infoTitle={translate(
          "Purchase amount - sale (w/o) VAT from delivery notes",
        )}
        loading={!!loader3}
        chartOptions={grossProfit}
        loginUserCurrency={loginUserCurrency}
        money
        centerSub="vs prior"
      />
      <OpsRingWidget
        title={translate("Vehicle Analytics")}
        infoTitle={translate(
          "Newly Created Vehicles In Last 30 Days VS Vehicles Created Prior Than Last 30 Days",
        )}
        loading={!!loader9}
        chartOptions={vehiclesData}
        centerSub="new VINs"
      />
      <OpsRingWidget
        title={translate("Fleet Service Throughput")}
        infoTitle={translate("Total DNs Count, Total Unique Vehicles Visits")}
        loading={!!loader8}
        chartOptions={fleetCountChart2}
        periodValue={tab1}
        onPeriodChange={(e) => handleChange("tab1", e)}
        centerSub="Visits Per Unit"
      />
      <OpsRingWidget
        title={translate("Fleet Actuation")}
        infoTitle={translate(
          "Total Unique Vehicles Visits VS Total Available Vehicles",
        )}
        loading={!!loader8}
        chartOptions={fleetCountChart}
        periodValue={tab1}
        onPeriodChange={(e) => handleChange("tab1", e)}
        centerSub="actuated"
      />
    </Box>

    <Box component="section" aria-label="Sales" sx={{ width: "100%" }}>
      <SalesChartWidget
        loading={!!loader4}
        nestedLoading={!!loader4a}
        translate={translate}
        cogsMonthly={cogsMonthly}
        date={date}
        tab2={tab2}
        onPeriodChange={(e) => handleChange("tab2", e)}
        onBack={handleCogsChartBack}
        chartData={cogsChartData}
        handleBarClickWrapper={handleBarClickWrapper}
        loginUserCurrency={loginUserCurrency}
        infoTitle={translate(
          "Monthly Sales With Respect To Services Packages And Items (with Vat)",
        )}
      />
    </Box>

    <Box component="section" aria-label="Breakdowns" sx={gridSx(GRID.three)}>
      <RankListWidget
        title={translate("Product Sales By Category")}
        infoTitle={translate("Total Sold Items And Their Count Top 20")}
        loading={!!loader16}
        chartOptions={sparePartsChartNew}
        periodValue={tab8}
        onPeriodChange={(e) => handleChange("tab8", e)}
        actionLabel={showAllInventory ? translate("Default") : translate("All")}
        onAction={handleShowAllInventoryChange}
      />
      <RankListWidget
        title={translate("Service By Type")}
        infoTitle={translate("Total Sold Services And Their Count")}
        loading={!!loader17}
        chartOptions={servicesPartsChartData}
        periodValue={tab9}
        onPeriodChange={(e) => handleChange("tab9", e)}
        actionLabel={showAllServices ? translate("Default") : translate("All")}
        onAction={handleShowAllServicesChange}
      />
      <RankListWidget
        title={translate("Vehicle Category By Type")}
        infoTitle={translate("Total Vehicles Count By Category")}
        loading={!!loader12}
        chartOptions={vehiclesChart}
        actionLabel={showAllVehicles ? translate("Default") : translate("All")}
        onAction={handleShowAllVehiclesChange}
      />
    </Box>

    <Box
      component="section"
      aria-label="Sales by region and collections"
      sx={gridSx(GRID.three)}
    >
      <NetSalesAnalyticsWidget
        loading={!!loader14}
        translate={translate}
        garageDataType={garageDataType}
        tab5={tab5}
        onPeriodChange={(e) => handleChange("tab5", e)}
        onBack={handleNetSalesAnalytics}
        verticalWiseChart={verticalWiseChart}
        areaWiseChart={areaWiseChart}
        stationWiseChart={stationWiseChart}
        areaWiseData={areaWiseData}
        loginUserCurrency={loginUserCurrency}
        infoTitle={translate("Vertical Wise Sale Weightage")}
      />
      <CollectionsOutstandingWidget
        loading={!!loader6b}
        translate={translate}
        chartOptions={collectionsAndOutStanding}
        totalValue={collectionsAndOutStandingData}
        loginUserCurrency={loginUserCurrency}
        infoTitle={translate(
          "Total Collected Amount VS Total Outstanding VS Total Advance Amount",
        )}
      />
      <UserLoginStatsWidget
        title={translate("User Login Stats")}
        infoTitle={translate("User Level Wise User Login")}
        loading={!!loader20}
        chartOptions={userLogsGraphData}
        periodValue={tab10}
        onPeriodChange={(e) => handleChange("tab10", e)}
      />
    </Box>

    <Box component="section" aria-label="Receivables" sx={gridSx(GRID.fin)}>
      <ReceivableAgingWidget
        loading={!!loader6}
        translate={translate}
        showAccountsData={showAccountsData}
        showAccountsTitle={showAccountsTitle}
        showAccountsLoader={showAccountsLoader}
        overDue={overDue}
        overDueChart={overDueChart}
        accountsDataChart={accountsDataChart}
        onBack={handleAccountsBack}
        loginUserCurrency={loginUserCurrency}
        infoTitle={translate(
          "Aging Summary Of Upto 360+ Days Of Receivable Amount",
        )}
      />
      <ClientOutstandingListWidget
        loading={!!loader7}
        translate={translate}
        outstandingSummary={outstandingSummary}
        loginUserCurrency={loginUserCurrency}
        infoTitle={translate("Total Due Amount Per Account")}
      />
      <StationUtilizationListWidget
        loading={!!loader11}
        translate={translate}
        topologyData={topologyData}
        loginUserCurrency={loginUserCurrency}
        infoTitle={translate("Workshop Wise Total DNs")}
      />
    </Box>
  </Box>
);

export default ExDashboardView;
